import { ChevronDown } from "lucide-react";
import type { ComponentPropsWithRef, CSSProperties, JSX } from "react";
import { cx } from "../cx";

export type SelectOption = string | { value: string; label: string };

export interface SelectProps extends Omit<ComponentPropsWithRef<"select">, "className" | "style"> {
  /** Caps 12px label rendered above the control. */
  label?: string | undefined;
  /** Options as strings or `{ value, label }` pairs. */
  options?: ReadonlyArray<SelectOption> | undefined;
  /** Class for the outer field wrapper. */
  className?: string | undefined;
  /** Style for the outer field wrapper. */
  style?: CSSProperties | undefined;
  /** Class for the select element itself. */
  selectClassName?: string | undefined;
  /** Style for the select element itself. */
  selectStyle?: CSSProperties | undefined;
}

/**
 * Native select with Tarski chrome — keeps the platform dropdown. Ported
 * from design/tarski-kit/components/forms/Select.jsx.
 */
export function Select({
  label,
  options = [],
  className,
  style,
  selectClassName,
  selectStyle,
  children,
  ...rest
}: SelectProps): JSX.Element {
  return (
    <label className={cx("tk-field", "tk-select", className)} style={style}>
      {label === undefined ? null : <span className="tk-field__label">{label}</span>}
      <span className="tk-select__frame">
        <select {...rest} className={cx("tk-select__control", selectClassName)} style={selectStyle}>
          {options.map((option) =>
            typeof option === "string" ? (
              <option key={option} value={option}>
                {option}
              </option>
            ) : (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ),
          )}
          {children}
        </select>
        <ChevronDown size={16} aria-hidden="true" className="tk-select__chevron" />
      </span>
    </label>
  );
}
